/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import {
  AddAlt,
  InformationFilled,
  TextFill,
  TrashCan
} from '@carbon/react/icons';
import {
  TextInput,
  Toggle,
  Button,
  Tag,
  RadioButtonGroup,
  RadioButton
} from 'carbon-components-react';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useIsNewIA } from '@ibm-watson/shared/modules/ia/hooks/useIsNewIA';
import { ctaEvents } from '@ibm-watson/shared/modules/ia/util/trackers/segment';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import { SEGMENT } from '../../../trackers/trackEvents';
import {
  HOME_SCREEN_BACKGROUND_OPTIONS,
  MAX_NUM_OF_STARTERS
} from '../../../util/validation/constants';
import { NAMESPACES, TOOLING_TYPES } from '../../../util/web-chat';
import { TabContent } from '../../common/tabbed-header/TabContent';
import PreviewWebChat from '../../preview-web-chat/PreviewWebChat';
import { links } from '../links';
import { HomeScreenTab as messages, Defaults } from '../messages';
import { FIELDS } from '../schema';

import './HomeScreen.scss';

interface HomeScreenProps {
  /**
   *
   */
  tabname?: string;
  /**
   *
   */
  environmentId?: string;
  /**
   *
   */
  integrationId: string;
  /**
   *
   */
  readOnly?: boolean;
}
export const HomeScreen = ({
  environmentId,
  integrationId,
  readOnly
}: HomeScreenProps) => {
  const { formatMessage: t } = useIntl();
  const formMethods = useFormContext();
  const webChatContainer = useRef(null);

  const { control } = formMethods;
  const { getFieldValue, updateFieldValue, registerInputField } =
    useFormHelpers(formMethods);

  const homeScreenGreetingChangeState = useState<boolean>(false);
  const [toggled, setToggled] = useState<boolean>(
    getFieldValue(FIELDS.HOME_SCREEN.IS_ON)
  );
  const [startersInputs, setStartersInputs] = useState([]);
  const [showAddButton, setShowAddButton] = useState(true);

  // Update startersEnabled state
  useEffect(() => {
    const starterArray =
      getFieldValue(FIELDS.HOME_SCREEN.STARTERS.BUTTONS) || [];
    setStartersInputs(starterArray);

    if (starterArray.length >= 5) {
      setShowAddButton(false);
    }

    if (starterArray.length <= 0) {
      updateFieldValue(FIELDS.HOME_SCREEN.STARTERS.IS_ON, false);
    }
  }, [getFieldValue, updateFieldValue]);

  const onToggle = boolean => {
    setToggled(boolean);
    ctaEvents.webChatHomeScreenToggle({
      category: 'HomeScreen',
      resultValue: boolean
        ? SEGMENT.EVENTS.WEB_CHAT_HOME_SCREEN_ENABLED
        : SEGMENT.EVENTS.WEB_CHAT_HOME_SCREEN_DISABLED
    });
  };

  const onDeleteStarter = index => {
    if (readOnly) {
      return;
    }
    const newStarters = startersInputs.slice();
    newStarters.splice(index, 1);
    const starterLength = newStarters.length;

    if (starterLength < 5 && !showAddButton) {
      setShowAddButton(true);
    }

    if (starterLength <= 0) {
      updateFieldValue(FIELDS.HOME_SCREEN.STARTERS.IS_ON, false);
    }

    updateFieldValue(FIELDS.HOME_SCREEN.STARTERS.BUTTONS, newStarters);
    setStartersInputs(newStarters);
  };

  const addNewStarter = () => {
    const starter_isOn = getFieldValue(FIELDS.HOME_SCREEN.STARTERS.IS_ON);
    const newStarters = startersInputs.slice();
    const previousLength = newStarters.length;
    const newStarter = {
      label: t(Defaults[`starter${previousLength}`]),
      value: ''
    };
    newStarters.push(newStarter);
    const starterLength = newStarters.length;

    if (starterLength >= 5) {
      setShowAddButton(false);
    }

    if (starterLength > 0 && !starter_isOn) {
      updateFieldValue(FIELDS.HOME_SCREEN.STARTERS.IS_ON, true);
    }

    updateFieldValue(FIELDS.HOME_SCREEN.STARTERS.BUTTONS, newStarters);
    setStartersInputs(newStarters);
  };

  return (
    <TabContent
      ImageComponent={() => <TextFill size={32} />}
      title={<FormattedMessage {...messages.title} />}
      subtitle={t(messages.subtitle)}
      learnMoreLink={
        useIsNewIA()
          ? links.learnMoreHomeScreenLinkNewIA
          : links.learnMoreHomeScreenLinkClassic
      }>
      <div className="HomeScreen__control">
        <Controller
          control={control}
          name={FIELDS.HOME_SCREEN.IS_ON}
          render={({ field: { onChange, value } }) => (
            <Toggle
              disabled={readOnly}
              className="TabContent__inner-field"
              id={FIELDS.HOME_SCREEN.IS_ON}
              onToggle={event => {
                onToggle(!value);
                onChange(event);
              }}
              toggled={value}
              aria-label={t(messages.enable)}
              size="sm"
            />
          )}
        />
        {toggled && (
          <Fragment>
            <div className="TabContent__field-container TabContent__field-container--full-width">
              <h4 className="TabContent__inner-title TabContent__inner-title-heading">
                <FormattedMessage {...messages.greetingTitle} />
              </h4>
              <p className="TabContent__inner-field TabContent__inner-field-body">
                <FormattedMessage {...messages.greetingDescription} />
              </p>
              <div className="TabContent__inner-field TabContent__inner-field-body">
                <InformationFilled
                  size={16}
                  className="TabContent__info-icon"
                  aria-label={t(messages.greetingNote)}
                />
                <span>
                  <FormattedMessage {...messages.greetingNote} />
                </span>
              </div>
              <TextInput
                {...registerInputField({
                  field: FIELDS.HOME_SCREEN.GREETING,
                  labelText: messages.greetingLabel,
                  changeState: homeScreenGreetingChangeState,
                  readOnly
                })}
                id={FIELDS.HOME_SCREEN.GREETING}
                className="TabContent__field"
              />
            </div>
            <div className="TabContent__field-container IntegrationPanel-Config__field-container--conversationStarters">
              <h4 className="TabContent__inner-title TabContent__inner-title-heading">
                <FormattedMessage {...messages.startersTitle} />
              </h4>
              <p className="TabContent__inner-field TabContent__inner-field-body">
                <FormattedMessage {...messages.startersDescription} />
              </p>
              {startersInputs.map((starter, i) => (
                <div className="TabContent__field-container" key={i}>
                  <div className="TabContent__starters-wrapper">
                    <Tag className="TabContent__starter-tag" type="gray">
                      {i + 1}
                    </Tag>
                    <TextInput
                      {...registerInputField({
                        field: FIELDS.HOME_SCREEN.STARTERS.GET_BUTTON_LABEL(i),
                        labelText: '',
                        readOnly
                      })}
                      key={i}
                      className="TabContent__field"
                      hideLabel
                      aria-label={t(messages.starterLabel, {
                        starterNum: i + 1
                      })}
                    />
                    <TrashCan
                      size={16}
                      className="TabContent__icon-delete"
                      id={`starter_delete-icon-${i}`}
                      onClick={() => onDeleteStarter(i)}
                    />
                  </div>
                </div>
              ))}
              {showAddButton && (
                <div className="TabContent__starter-addButton-wrapper">
                  <Button
                    className="TabContent__starter-addButton"
                    kind="ghost"
                    disabled={readOnly}
                    onClick={addNewStarter}>
                    <AddAlt size={16} />
                    <p>{t(messages.startersButtonText)}</p>
                    <p className="TabContent__starter-maxNum">
                      ({startersInputs.length}/{MAX_NUM_OF_STARTERS})
                    </p>
                  </Button>
                </div>
              )}
            </div>
            {!getFieldValue(FIELDS.THEME_CONFIG.USE_AI_THEME) && (
              <div className="TabContent__field-container">
                <h4 className="TabContent__inner-title TabContent__inner-title-heading">
                  <FormattedMessage {...messages.backgroundTitle} />
                </h4>
                <Controller
                  control={control}
                  name={FIELDS.HOME_SCREEN.BACKGROUND}
                  render={({ field: { onChange, value } }) => {
                    return (
                      <RadioButtonGroup
                        className="Background_radio-group"
                        disabled={readOnly}
                        name={FIELDS.HOME_SCREEN.BACKGROUND}
                        legendText={t(messages.backgroundLabel)}
                        orientation="vertical"
                        valueSelected={value}>
                        <RadioButton
                          id="background_option_none"
                          value={HOME_SCREEN_BACKGROUND_OPTIONS.NONE}
                          labelText={t(messages.backgroundOptionNone)}
                          onClick={e =>
                            onChange((e.target as HTMLTextAreaElement).value)
                          }
                        />
                        <RadioButton
                          id="background_option_solid"
                          value={HOME_SCREEN_BACKGROUND_OPTIONS.SOLID}
                          labelText={t(messages.backgroundOptionSolid)}
                          onClick={e =>
                            onChange((e.target as HTMLTextAreaElement).value)
                          }
                        />
                        <RadioButton
                          id="background_option_top_corner"
                          value={HOME_SCREEN_BACKGROUND_OPTIONS.TOP_CORNER_OUT}
                          labelText={t(
                            messages.backgroundOptionGradientTopCornerOut
                          )}
                          onClick={e =>
                            onChange((e.target as HTMLTextAreaElement).value)
                          }
                        />
                        <RadioButton
                          id="background_option_bottom_up"
                          value={HOME_SCREEN_BACKGROUND_OPTIONS.BOTTOM_UP}
                          labelText={t(
                            messages.backgroundOptionGradientBottomUp
                          )}
                          onClick={e =>
                            onChange((e.target as HTMLTextAreaElement).value)
                          }
                        />
                      </RadioButtonGroup>
                    );
                  }}
                />
              </div>
            )}
          </Fragment>
        )}
      </div>
      <div className="HomeScreen__webchat">
        <PreviewWebChat
          integrationId={integrationId}
          containerRef={webChatContainer}
          environmentId={environmentId}
          namespace={NAMESPACES.CHANNEL_PAGE}
          removeDefaultFocus={true}
          showRestart={true}
          toolingType={TOOLING_TYPES.WEB_CHAT}
          useDraftConfig
          disableSessionHistory
        />
        <div className="container" ref={webChatContainer} />
      </div>
    </TabContent>
  );
};
